import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { Project, FundRequest } from '../../types';
import apiService from '../../services/api';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHead
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaMoneyBillWave, 
  FaArrowUp, 
  FaArrowDown, 
  FaChartLine,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaDownload,
  FaFilter,
  FaSearch
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    font-size: 1.8rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StatCard = styled(Card)`
  background: linear-gradient(135deg, var(--primary-color) 0%, #e07a10 100%);
  color: white;
  border: none;
  
  .stat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  
  .stat-icon {
    font-size: 2rem;
    opacity: 0.8;
  }
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
  }
  
  .stat-change {
    font-size: 0.8rem;
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  
  &.income {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  }
  
  &.expense {
    background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
  }
  
  &.balance {
    background: linear-gradient(135deg, #007bff 0%, #6610f2 100%);
  }
  
  &.budget {
    background: linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%);
  }
`;

const SectionCard = styled(Card)`
  margin-bottom: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    color: var(--secondary-color);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--medium-gray);
  border-radius: var(--border-radius);
  background: white;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--medium-gray);
  border-radius: var(--border-radius);
  background: white;
  font-size: 0.9rem;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const TransactionTable = styled(Table)`
  width: 100%;
  
  th, td {
    padding: 1rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--light-gray);
  }
  
  th {
    background: var(--light-gray);
    font-weight: 600;
    color: var(--secondary-color);
  }
  
  tr:hover {
    background: var(--light-gray);
  }
  
  .amount {
    font-weight: 600;
  }
  
  .amount.positive {
    color: var(--success-color);
  }
  
  .amount.negative {
    color: var(--danger-color);
  }
  
  .status {
    padding: 0.25rem 0.5rem;
    border-radius: var(--border-radius);
    font-size: 0.8rem;
    font-weight: 500;
  }
  
  .status.approved {
    background: var(--success-color);
    color: white;
  }
  
  .status.pending {
    background: var(--warning-color);
    color: white;
  }
  
  .status.paid {
    background: var(--info-color);
    color: white;
  }
  
  .status.rejected {
    background: var(--danger-color);
    color: white;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--dark-gray);
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: var(--secondary-color);
  }
  
  p {
    opacity: 0.7;
  }
`;

interface Transaction {
  id: string;
  date: string;
  description: string;
  type: 'income' | 'expense';
  amount: number;
  category: string;
  status: string;
  projectId: string;
}

const SupervisorAccounting: React.FC = () => {
  const { user } = useAuth();
  const [assignedProject, setAssignedProject] = useState<Project | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchAssignedProject = async () => {
      try {
        // Get projects assigned to the current supervisor
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        // Backend now properly filters by role, so we can use the first project
        const supervisorProject = assignedProjects.length > 0 ? assignedProjects[0] : null;
        
        if (supervisorProject) {
          setAssignedProject(supervisorProject);
          await generateTransactions(supervisorProject);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching assigned project:", error);
        setLoading(false);
      }
    };

    if (user) {
      fetchAssignedProject();
    }
  }, [user]);

  const generateTransactions = async (project: Project) => {
    try {
      const projectTransactions: Transaction[] = [];
      
      // Get fund requests for this project
      const fundRequestsResponse = await apiService.getFundRequests({ 
        projectId: project._id || project.id 
      });
      const projectRequests = fundRequestsResponse.data || [];
      
      // Get paid fund requests for this project
      const paidRequests = projectRequests.filter((req: any) => 
        req.projectId === (project._id || project.id) && req.status === 'Paid'
      );
      
      paidRequests.forEach((request: any, index: number) => {
        const totalAmount = request.items?.reduce((sum: number, item: any) => sum + item.totalPrice, 0) || request.amount;
        projectTransactions.push({
          id: `income-${index}`,
          date: new Date(request.createdAt || Date.now() - index * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: `Fund Request - ${request.items?.[0]?.description || 'Fund Request'}`,
          type: 'income',
          amount: totalAmount,
          category: 'Others',
          status: 'paid',
          projectId: project._id || project.id || ''
        });
      });
      
      // Get expenses for this project
      const expensesResponse = await apiService.getExpenses({ 
        projectId: project._id || project.id 
      });
      const projectExpenses = expensesResponse.data || [];
      
      // Add expense transactions
      projectExpenses.forEach((expense: any, index: number) => {
        projectTransactions.push({
          id: `expense-${index}`,
          date: new Date(expense.date || Date.now() - (index * 2 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
          description: expense.description || `${expense.category} Purchase`,
          type: 'expense',
          amount: expense.amount,
          category: expense.category,
          status: expense.status || 'approved',
          projectId: project._id || project.id || ''
        });
      });
      
      // Sort by date (newest first)
      projectTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setTransactions(projectTransactions);
      setFilteredTransactions(projectTransactions);
    } catch (error) {
      console.error("Error generating transactions:", error);
    }
  };

  useEffect(() => {
    let filtered = transactions;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type === filterType);
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(t => t.category === filterCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTransactions(filtered);
  }, [transactions, filterType, filterCategory, searchTerm]);

  const calculateStats = () => {
    const income = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const balance = income - expenses;
    const budget = assignedProject?.budget?.total || 0;
    const remainingBudget = budget - expenses;
    
    return { income, expenses, balance, budget, remainingBudget };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <h3>Loading...</h3>
        </div>
      </PageContainer>
    );
  }

  if (!assignedProject) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>Project Accounting</h1>
        </PageHeader>
        
        <NoDataMessage>
          <div className="icon">🏗️</div>
          <h3>No Project Assigned</h3>
          <p>You are not currently assigned to supervise any project. Please contact your project manager or administrator.</p>
        </NoDataMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Project Accounting</h1>
        <ActionButtons>
          <Button>
            <IconWrapper icon={FaDownload} />
            Export Report
          </Button>
        </ActionButtons>
      </PageHeader>

      <StatsGrid>
        <StatCard className="income">
          <div className="stat-header">
            <div>
              <div className="stat-label">Total Income</div>
              <div className="stat-value">TZS {stats.income.toLocaleString()}</div>
            </div>
            <IconWrapper icon={FaArrowUp} className="stat-icon" />
          </div>
          <div className="stat-change">
            <IconWrapper icon={FaChartLine} />
            From Fund Requests
          </div>
        </StatCard>

        <StatCard className="expense">
          <div className="stat-header">
            <div>
              <div className="stat-label">Total Expenses</div>
              <div className="stat-value">TZS {stats.expenses.toLocaleString()}</div>
            </div>
            <IconWrapper icon={FaArrowDown} className="stat-icon" />
          </div>
          <div className="stat-change">
            <IconWrapper icon={FaChartLine} />
            Project Spending
          </div>
        </StatCard>

        <StatCard className="balance">
          <div className="stat-header">
            <div>
              <div className="stat-label">Current Balance</div>
              <div className="stat-value">TZS {stats.balance.toLocaleString()}</div>
            </div>
            <IconWrapper icon={FaMoneyBillWave} className="stat-icon" />
          </div>
          <div className="stat-change">
            <IconWrapper icon={FaChartLine} />
            Available Funds
          </div>
        </StatCard>

        <StatCard className="budget">
          <div className="stat-header">
            <div>
              <div className="stat-label">Remaining Budget</div>
              <div className="stat-value">TZS {stats.remainingBudget.toLocaleString()}</div>
            </div>
            <IconWrapper icon={FaFileInvoiceDollar} className="stat-icon" />
          </div>
          <div className="stat-change">
            <IconWrapper icon={FaChartLine} />
            Budget vs Spending
          </div>
        </StatCard>
      </StatsGrid>

      <SectionCard>
        <CardHeader>
          <SectionHeader>
            <h3>
              <IconWrapper icon={FaFileInvoiceDollar} />
              Transaction History
            </h3>
            <div>
              <span style={{ fontSize: '0.9rem', color: 'var(--dark-gray)' }}>
                {filteredTransactions.length} transactions
              </span>
            </div>
          </SectionHeader>
        </CardHeader>
        
        <CardBody>
          <FilterControls>
            <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="income">Income</option>
              <option value="expense">Expenses</option>
            </Select>
            
            <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">All Categories</option>
              <option value="Materials">Materials</option>
              <option value="Labor">Labor</option>
              <option value="Equipment">Equipment</option>
              <option value="Sub-contracts">Sub-contracts</option>
              <option value="Others">Others</option>
            </Select>
            
            <SearchInput 
              type="text" 
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterControls>

          {filteredTransactions.length > 0 ? (
            <TransactionTable>
              <TableHead>
                <TableRow>
                  <TableHeader>Date</TableHeader>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Status</TableHeader>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <IconWrapper icon={FaCalendarAlt} style={{ fontSize: '0.8rem', opacity: 0.7 }} />
                        {new Date(transaction.date).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>{transaction.category}</TableCell>
                    <TableCell>
                      <span style={{ 
                        color: transaction.type === 'income' ? 'var(--success-color)' : 'var(--danger-color)',
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {transaction.type}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`amount ${transaction.type === 'income' ? 'positive' : 'negative'}`}>
                        {transaction.type === 'income' ? '+' : '-'} TZS {transaction.amount.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`status ${transaction.status}`}>
                        {transaction.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </TransactionTable>
          ) : (
            <NoDataMessage>
              <div className="icon">📊</div>
              <h3>No Transactions Found</h3>
              <p>No transactions match your current filters. Try adjusting your search criteria.</p>
            </NoDataMessage>
          )}
        </CardBody>
      </SectionCard>
    </PageContainer>
  );
};

export default SupervisorAccounting; 